"use client"

import { ChevronRight, Eye, EyeOff, type LucideIcon } from "lucide-react"
import { useNavigate, useLocation } from "react-router-dom"
import { useState, useEffect } from "react"
import { useEntitySettingsModalStore } from "@/store/entitySettingsModal.store"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "../ui/sidebar"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip"
import { Badge } from "../ui/badge"

export function NavMain({
  items,
  onComplianceToggle,
  showAllComplianceItems,
  disableTooltips = false,
}: {
  items: {
    title: string
    url: string
    icon?: LucideIcon
    isActive?: boolean
    items?: {
      title: string
      url: string
      disabled?: boolean
      count?: number // Add optional count for badge display
      issueType?: string // Add optional issue type for identification
    }[]
  }[]
  onComplianceToggle?: (showAll: boolean) => void
  showAllComplianceItems?: boolean
  disableTooltips?: boolean // Disables tooltips in hover overlay mode to prevent visual overlap with menu content
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = useSidebar()
  const openEntitySettings = useEntitySettingsModalStore(s => s.openForSelectedEntity)

  // State to track which menu items are expanded
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({})

  // Load expanded state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem('sidebar-expanded-items')
    if (savedState) {
      try {
        setExpandedItems(JSON.parse(savedState))
      } catch (error) {
        console.warn('Failed to parse saved sidebar state:', error)
      }
    }
  }, [])

  // Save expanded state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebar-expanded-items', JSON.stringify(expandedItems))
  }, [expandedItems])

  // Function to toggle item expansion
  const toggleItem = (itemTitle: string, isOpen: boolean) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemTitle]: isOpen
    }))
  }

  // Function to get initial open state for an item
  const getInitialOpenState = (item: typeof items[0]) => {
    // First check localStorage state
    if (expandedItems.hasOwnProperty(item.title)) {
      return expandedItems[item.title]
    }
    // Fallback to route-based logic for initial load
    return item.isActive || location.pathname.startsWith(item.url)
  }

  return (
    <SidebarGroup>
      <SidebarMenu className="space-y-1">
        {items.map((item) => (
          <Collapsible
            key={item.title}
            asChild
            open={getInitialOpenState(item)}
            onOpenChange={(isOpen) => toggleItem(item.title, isOpen)}
            className="group/collapsible"
          >
            <SidebarMenuItem className="relative group/menuitem">
              <div className="flex items-center w-full">
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip={disableTooltips ? undefined : item.title}
                    className="h-10 px-3 font-medium hover:bg-sidebar-accent data-[state=open]:bg-sidebar-accent/50 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex-1"
                  >
                    {item.icon && <item.icon className="h-4 w-4" />}
                    <span className="text-sm">{item.title}</span>
                    <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>

                {/* Separate toggle button for LEDGER REPAIR and AML DECISIONS groups to avoid button nesting */}
                {(item.title === "LEDGER REPAIR" || item.title === "AML DECISIONS") && onComplianceToggle && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (typeof onComplianceToggle === 'function') {
                        onComplianceToggle(!showAllComplianceItems);
                      }
                    }}
                    className="ml-1 p-1 rounded hover:bg-sidebar-accent-foreground/10 transition-colors cursor-pointer h-8 w-8 flex items-center justify-center shrink-0"
                    title={showAllComplianceItems ? "Hide zero count items" : "Show all items"}
                    aria-label={showAllComplianceItems ? "Hide zero count items" : "Show all items"}
                  >
                    {showAllComplianceItems ? (
                      <EyeOff className="h-3 w-3 opacity-70" />
                    ) : (
                      <Eye className="h-3 w-3 opacity-70" />
                    )}
                  </button>
                )}
              </div>
              <CollapsibleContent>
                <SidebarMenuSub className="ml-2 mt-1 space-y-1">
                  {item.items?.map((subItem) => (
                    <SidebarMenuSubItem key={subItem.title}>
                      {subItem.disabled ? (
                        <SidebarMenuSubButton
                          aria-disabled={subItem.disabled}
                          className="h-9 px-1 text-sm font-normal text-muted-foreground cursor-not-allowed opacity-50"
                        >
                          <div
                            className="flex items-center justify-between w-full"
                            title={subItem.count === 0 ? "No issues found for this check type" : undefined}
                          >
                            <span>{subItem.title}</span>
                            {typeof subItem.count === 'number' && subItem.count > 0 && (
                              <Badge variant="outline" className="text-xs ml-2 h-5 px-1.5">
                                {subItem.count}
                              </Badge>
                            )}
                          </div>
                        </SidebarMenuSubButton>
                      ) : (
                        <SidebarMenuSubButton
                          asChild
                          className="h-9 px-1 text-sm font-normal hover:bg-sidebar-accent cursor-pointer"
                        >
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              if (subItem.url === 'action:entity-settings') {
                                openEntitySettings()
                                return
                              }
                              navigate(subItem.url)
                              // Prevent any default sidebar collapse behavior
                            }}
                            className="w-full text-left transition-colors duration-200 flex items-center justify-between"
                          >
                            <span>{subItem.title}</span>
                            {typeof subItem.count === 'number' && subItem.count > 0 && (
                              <Badge variant="outline" className="text-xs ml-2 h-5 px-1.5">
                                {subItem.count}
                              </Badge>
                            )}
                          </button>
                        </SidebarMenuSubButton>
                      )}
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
              {/* No submenu when collapsed - just hide it completely */}
            </SidebarMenuItem>
          </Collapsible>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}
